import * as React from "react"
import { Search } from "lucide-react"

import { cn } from "../../../lib/utils"
import { Input, type InputProps } from "../Input"

export interface SearchInputProps extends Omit<InputProps, "type" | "leadingIcon"> {
  /**
   * Callback when search is submitted (Enter key pressed)
   */
  onSearch?: (value: string) => void
}

/**
 * SearchInput component with search icon and clearable functionality.
 */
function SearchInput({
  className,
  size,
  onSearch,
  onKeyDown,
  onClear,
  clearable = true,
  ...props
}: SearchInputProps) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && onSearch) {
      onSearch((e.target as HTMLInputElement).value)
    }
    onKeyDown?.(e)
  }

  const handleClear = () => {
    onClear?.()
    onSearch?.("")
  }

  const iconSizeClass = size === "sm" ? "size-4" : size === "lg" ? "size-6" : "size-5"

  return (
    <Input
      type="search"
      data-slot="search-input"
      size={size}
      leadingIcon={<Search className={iconSizeClass} />}
      clearable={clearable}
      onKeyDown={handleKeyDown}
      onClear={handleClear}
      className={cn(
        // Hide browser default clear button since we have our own
        "[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden",
        className
      )}
      {...props}
    />
  )
}

SearchInput.displayName = "SearchInput"

export { SearchInput }
